import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'
import type {
  RecommendGifsRequest,
  RecommendGifsResponse,
  RecommendationCandidate,
  RecommendationStanceView,
  RetrieveCandidatePoolRequest,
  RetrieveCandidatePoolResponse,
  ExpandRecommendationStanceRequest,
  ExpandRecommendationStanceResponse,
  DraftFixtureMeta,
  DraftFixtureMetaRequest,
  DraftFixtureMetaResponse,
  FixtureNoteReaction,
  FixtureNoteStanceReaction,
  TokenUsageInfo,
} from '../../../../functions/src/shared/types/gif'
import { ExplicitRating } from '../../../../functions/src/shared/types/gif'
import { TokenService } from '../../../services/tokenService'

/**
 * Get GIF recommendations based on conversation context and user intent
 * @param conversationSnippet - Optional snippet of conversation context
 * @param userIntent - What the user wants to convey
 * @param preferences - Optional user preferences and filters
 * @param explicitRating - Optional explicit rating filter
 * @param options - Optional preset override (admin testing & eval tooling)
 * @returns Promise<RecommendationResult> - Array of ranked GIF recommendations
 */
export const getRecommendations = async (
  conversationSnippet?: string,
  userIntent?: string,
  preferences?: RecommendGifsRequest['preferences'],
  explicitRating?: ExplicitRating,
  options?: {
    preset?: string
  }
): Promise<{
  recommendations: RecommendationCandidate[]
  stances?: RecommendationStanceView[]
  totalCandidatesEvaluated: number
  processingTimeMs: number
  timingBreakdown: {
    documentFetchMs: number
    aiProcessingMs: number
  }
  tokenUsage?: {
    processing?: TokenUsageInfo
  }
}> => {
  // Validate input - at least one context field is required
  if (!conversationSnippet && !userIntent) {
    throw new Error(
      'Either conversation snippet or user intent must be provided'
    )
  }

  // Generate request ID for logging
  const requestId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  try {
    const recommendGifsFunction = httpsCallable<
      RecommendGifsRequest,
      RecommendGifsResponse
    >(functions, 'recommendGifs', {
      timeout: 170000, // 170 seconds (10s less than server timeout of 180s)
    })

    // Build the explicitRating array - include all ratings up to and including the selected one
    const getExplicitRatingFilter = (
      selectedRating?: ExplicitRating
    ): ExplicitRating[] => {
      if (!selectedRating) {
        return [ExplicitRating.G, ExplicitRating.PG] // Default safe ratings
      }

      const allRatings = [
        ExplicitRating.G,
        ExplicitRating.PG,
        ExplicitRating.PG_13,
        ExplicitRating.R,
      ]
      const selectedIndex = allRatings.indexOf(selectedRating)

      if (selectedIndex === -1) {
        return [ExplicitRating.G, ExplicitRating.PG] // Fallback to safe ratings
      }

      return allRatings.slice(0, selectedIndex + 1)
    }

    const request: RecommendGifsRequest = {
      context: {
        conversationSnippet,
        userIntent,
      },
      preferences: {
        maxResults: 8,
        explicitRating: getExplicitRatingFilter(explicitRating),
        ...preferences,
      },
      ...(options?.preset ? { preset: options.preset } : {}),
      logging: {
        enabled: true,
        requestId, // Include requestId for progress tracking
      },
    }

    const result = await recommendGifsFunction(request)

    if (!result.data.success) {
      throw new Error(result.data.error || 'Recommendation request failed')
    }

    if (!result.data.data) {
      throw new Error('No recommendation data returned')
    }

    return result.data.data
  } catch (error) {
    console.error('Error getting recommendations:', error)

    // Handle rate limit errors specifically
    if (TokenService.isRateLimitError(error)) {
      const rateLimitError = TokenService.parseRateLimitError(error)
      throw rateLimitError
    }

    throw new Error(
      `Failed to get recommendations: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Fetch an embedding-retrieval candidate pool for a conversation — used while
 * authoring eval fixtures on /admin/eval-fixtures to surface candidates from
 * the whole enriched library instead of only the recommender's own picks.
 * Fast: one embedding call, no LLM.
 */
export const getRetrievalPool = async (
  conversationSnippet?: string,
  userIntent?: string,
  k = 24
): Promise<{
  candidates: { gifId: string; score: number }[]
  retrievalRecordCount: number
}> => {
  if (!conversationSnippet && !userIntent) {
    throw new Error(
      'Either conversation snippet or user intent must be provided'
    )
  }

  const retrievePoolFunction = httpsCallable<
    RetrieveCandidatePoolRequest,
    RetrieveCandidatePoolResponse
  >(functions, 'retrieveCandidatePool', { timeout: 25000 })

  const result = await retrievePoolFunction({
    context: { conversationSnippet, userIntent },
    preferences: {
      // The server reads element [0] as the MAX allowed rating.
      explicitRating: [ExplicitRating.PG_13],
    },
    k,
  })

  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Retrieval pool request failed')
  }
  return result.data.data
}

/**
 * Get similar GIF recommendations based on an existing GIF
 * @param gifId - The ID of the GIF to find similar recommendations for
 * @param preferences - Optional user preferences and filters
 * @returns Promise<RecommendationResult> - Array of similar GIF recommendations
 */
export const getSimilarRecommendations = async (
  gifId: string,
  preferences?: RecommendGifsRequest['preferences']
): Promise<{
  recommendations: RecommendationCandidate[]
  totalCandidatesEvaluated: number
  processingTimeMs: number
  timingBreakdown: {
    documentFetchMs: number
    aiProcessingMs: number
  }
  tokenUsage?: {
    processing?: TokenUsageInfo
  }
}> => {
  // Generate request ID for logging
  const requestId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  try {
    const recommendGifsFunction = httpsCallable<
      RecommendGifsRequest,
      RecommendGifsResponse
    >(functions, 'recommendGifs', {
      timeout: 170000, // 170 seconds (10s less than server timeout of 180s)
    })

    const request: RecommendGifsRequest = {
      refinement: {
        similarToGifId: gifId,
      },
      preferences: preferences || {
        maxResults: 8,
        explicitRating: [ExplicitRating.G, ExplicitRating.PG],
      },
      logging: {
        enabled: true,
        requestId, // Include requestId for progress tracking
      },
    }

    const result = await recommendGifsFunction(request)

    if (!result.data.success) {
      throw new Error(
        result.data.error || 'Similar recommendations request failed'
      )
    }

    if (!result.data.data) {
      throw new Error('No similar recommendation data returned')
    }

    return result.data.data
  } catch (error) {
    console.error('Error getting similar recommendations:', error)

    // Handle rate limit errors specifically
    if (TokenService.isRateLimitError(error)) {
      const rateLimitError = TokenService.parseRateLimitError(error)
      throw rateLimitError
    }

    throw new Error(
      `Failed to get similar recommendations: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Expand a single stance ("more like this"): fetch fresh embedding-neighbors of
 * the stance's current gifs, excluding everything already shown. Zero LLM
 * calls server-side, so it's fast and free.
 *
 * @param gifIds - The stance's current gif ids (seeds, 1–5)
 * @param excludeGifIds - Every gif id already displayed across all stances
 * @param limit - How many to fetch (default 4; server clamps 1–8)
 * @returns Fresh candidates to append to the stance group
 */
export const expandRecommendationStance = async (
  gifIds: string[],
  excludeGifIds: string[],
  limit = 4
): Promise<{ recommendations: RecommendationCandidate[] }> => {
  const expandFunction = httpsCallable<
    ExpandRecommendationStanceRequest,
    ExpandRecommendationStanceResponse
  >(functions, 'expandRecommendationStance', { timeout: 25000 })

  const result = await expandFunction({
    gifIds,
    excludeGifIds,
    limit,
    // The server reads element [0] as the MAX allowed rating.
    contentRatings: [ExplicitRating.PG_13],
  })

  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Stance expansion request failed')
  }
  return result.data.data
}

/**
 * Draft a full eval fixture — the load-bearing `note` plus a proposed
 * slug/description/tags — from a curator's framed conversation and bucketed
 * trap/forbidden/good gifs (plus optional Explore & react reactions). Used by
 * the /admin/eval-fixtures authoring flow. One Sonnet 4.6 LLM call server-side
 * (a few seconds), so a 60s timeout.
 *
 * @param context - The conversation/intent the fixture is about (≥1 required)
 * @param options - The bucketed gifs and/or reactions to synthesize, optional
 *   stance-level reactions, and an existingNote to revise. Provide at least one
 *   bucketed gif, a non-skip reaction, or an existingNote.
 * @returns The drafted fixture metadata for the author to edit and approve
 */
export const draftFixtureMeta = async (
  context: DraftFixtureMetaRequest['context'],
  options?: {
    buckets?: DraftFixtureMetaRequest['buckets']
    reactions?: FixtureNoteReaction[]
    stanceReactions?: FixtureNoteStanceReaction[]
    existingNote?: string
  }
): Promise<DraftFixtureMeta> => {
  const draftFn = httpsCallable<
    DraftFixtureMetaRequest,
    DraftFixtureMetaResponse
  >(functions, 'draftFixtureMeta', { timeout: 60000 })

  const result = await draftFn({
    context,
    ...(options?.buckets && options.buckets.length > 0
      ? { buckets: options.buckets }
      : {}),
    ...(options?.reactions && options.reactions.length > 0
      ? { reactions: options.reactions }
      : {}),
    ...(options?.stanceReactions
      ? { stanceReactions: options.stanceReactions }
      : {}),
    ...(options?.existingNote ? { existingNote: options.existingNote } : {}),
  })

  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Fixture drafting failed')
  }
  return result.data.data
}
